"use client";

import React from "react";
import {
  calculateDealerPrice,
  formatWithCommasWithoutDecimal,
} from "@/utils/helpers";
import {
  Box,
  Card,
  CardContent,
  Grid,
  SwipeableDrawer,
  Typography,
} from "@mui/material";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import Image from "next/image";
import { useAppSelector } from "@/hooks/useAppSelector";
import { motion } from "framer-motion";

const IMAGE_BASE = process.env.NEXT_PUBLIC_IMAGE_BASE;

interface ViewMyDiscountProps {
  open: boolean;
  handleClose: () => void;
}

const MobileViewMyDiscount: React.FC<ViewMyDiscountProps> = ({
  open,
  handleClose,
}) => {
  const { cart } = useAppSelector((state) => state.cart);
  const { manufacturers } = useAppSelector((state) => state.home);

  const allManufacturers = [
    ...(manufacturers?.domestic || []),
    ...(manufacturers?.premium || []),
    ...(manufacturers?.others || []),
  ];

  const discountData = allManufacturers
    ?.filter(
      (brand) => brand?.MOQ_slab !== null && brand?.MOQ_slab !== undefined
    )
    ?.map((brand) => {
      const cartBrand = cart?.manufacturers?.find(
        (m) => m?.manufacture_name === brand?.manufacture_name
      );

      const calcAmount = (type: "addedAmount" | "discountAdded") => {
        return cartBrand
          ? cartBrand.products?.reduce((acc, p) => {
              const ndpPrice = calculateDealerPrice(
                Number(p.MRP_price),
                Number(
                  p?.buy_quantity &&
                    p?.Sales_Qunatity !== null &&
                    p?.Sales_Qunatity !== 0 &&
                    p?.Sales_Price !== null &&
                    Number(p?.Sales_Price) !== 0 &&
                    (type === "addedAmount" ||
                      p?.buy_quantity >= p?.Sales_Qunatity)
                    ? p?.Sales_Price
                    : p?.NDP_Price
                ),
                p.GST
              );
              return acc + ndpPrice * (p?.buy_quantity || 0);
            }, 0)
          : 0;
      };

      return {
        brand: brand?.manufacture_name,
        brandLogo: brand?.manufacture_images
          ? `https://cyclecircle.blr1.cdn.digitaloceanspaces.com/${IMAGE_BASE}_MANUFACTURE_IMAGE/${brand?.manufacture_images}`
          : "",
        discountSlab: brand?.additional_discount_slab
          ? `${
              brand?.discount
            }% discount for orders above ₹${formatWithCommasWithoutDecimal(
              brand?.additional_discount_slab
            )}`
          : "No discount available",
        addedAmount: calcAmount("addedAmount"),
        minAmount: brand?.MOQ_slab || 0,
        discountAdded: calcAmount("discountAdded"),
        discountMin: brand?.additional_discount_slab || 0,
      };
    });

  return (
    <SwipeableDrawer
      anchor="right"
      open={open}
      onClose={handleClose}
      onOpen={handleClose}
      sx={{
        "& .MuiDrawer-paper": {
          width: "100%",
          maxWidth: "450px",
          borderTopLeftRadius: "10px",
          borderBottomLeftRadius: "10px",
          padding: "16px",
        },
      }}
    >
      <Box className="relative p-4 border-b border-gray-200">
        <Typography variant="h6" className="text-lg font-bold text-black-1">
          🎯 Slab Discount Overview
        </Typography>
        <CloseOutlinedIcon
          className="text-black-1 absolute top-4 right-4 cursor-pointer"
          onClick={handleClose}
          style={{ fontSize: "20px" }}
        />
      </Box>

      <Grid container spacing={2} className="p-2">
        {discountData?.map((row, index) => {
          const slabMet = row?.addedAmount >= row?.minAmount;
          const discountMet = row?.discountAdded >= row?.discountMin;

          return (
            <Grid item xs={12} key={index}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
              >
                <Card className="shadow-md rounded-xl p-2">
                  <CardContent>
                    <Box className="flex items-center gap-3 mb-3">
                      {row.brandLogo ? (
                        <Image
                          src={row.brandLogo}
                          alt={row.brand}
                          width={80}
                          height={30}
                          className="object-contain"
                        />
                      ) : (
                        <Typography className="font-semibold text-sm text-black">
                          {row.brand}
                        </Typography>
                      )}
                    </Box>

                    {[
                      {
                        label: "MOQ Progress",
                        value: row.addedAmount,
                        total: row.minAmount,
                        met: slabMet,
                        metText: "🎉 Congratulations! MOQ Met",
                        unmetText: "🛒 Add more items to reach MOQ",
                      },
                      {
                        label: "Discount Progress",
                        value: row.discountAdded,
                        total: row.discountMin,
                        met: discountMet,
                        metText: `💰 Discount Unlocked: ₹${formatWithCommasWithoutDecimal(
                          (row?.discountAdded * 2) / 100
                        )}`,
                        unmetText: row.discountSlab,
                      },
                    ].map((section, i) => (
                      <Box
                        key={i}
                        className={`${
                          section.met ? "bg-green-50" : "bg-gray-100"
                        } rounded-md p-3 mb-2 transition-all`}
                      >
                        <Typography className="text-sm font-medium text-black-1">
                          ₹{formatWithCommasWithoutDecimal(section.value)} / ₹
                          {formatWithCommasWithoutDecimal(section.total)}
                        </Typography>

                        <div className="w-full h-2 rounded-md my-2 bg-gray-200 overflow-hidden">
                          <motion.div
                            className="h-full rounded-md bg-gradient-to-r from-green-400 via-green-500 to-green-600"
                            initial={{ width: 0 }}
                            animate={{
                              width: `${Math.min(
                                (section.value / section.total) * 100,
                                100
                              )}%`,
                            }}
                            transition={{ duration: 0.8, ease: "easeInOut" }}
                          />
                        </div>

                        <Typography
                          className={`text-xs mt-1 ${
                            section.met
                              ? "text-green-700 font-semibold"
                              : "text-gray-600"
                          }`}
                        >
                          {section.met ? section.metText : section.unmetText}
                        </Typography>
                      </Box>
                    ))}
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          );
        })}
      </Grid>
    </SwipeableDrawer>
  );
};

export default MobileViewMyDiscount;
